"use client";

import { useState } from "react";
import Link from "next/link";
import {
  Activity,
  BarChart3,
  CheckCircle2,
  GitBranch,
  MonitorCheck,
  ShieldCheck,
  Timer,
  Wrench,
  Zap,
} from "lucide-react";

const smallCompanyBenefits = [
  "Kompletter Neuaufbau Ihrer bestehenden Website ohne Baukasten.",
  "Schnellere Ladezeiten und bessere Sichtbarkeit bei Google in Ihrer Region.",
  "Mehr Anfragen durch klare Inhalte, Struktur und Nutzerführung.",
  "Sie müssen sich um Technik nicht kümmern – wir übernehmen Betrieb und Monitoring.",
];

const enterpriseStack = [
  { icon: GitBranch, label: "CI/CD-Pipelines und automatisierte Deployments" },
  { icon: ShieldCheck, label: "Testgetriebene Entwicklung und Code-Reviews" },
  { icon: Timer, label: "Performance-Optimierung (Core Web Vitals)" },
  { icon: MonitorCheck, label: "Monitoring, Tracking und Alarmierung" },
  { icon: Activity, label: "Automatisierung für Ausfälle, Updates und Backups" },
  { icon: Wrench, label: "Saubere SEO-Strukturen und strukturierte Daten" },
];

const wordpressLimits = [
  "Baukästen und WordPress sind oft zu langsam und nicht sauber optimiert.",
  "Abhängigkeit von Plugins macht Wartung, Sicherheit und Performance schwierig.",
  "Die Codebasis ist häufig unübersichtlich und schwer zu kontrollieren.",
  "Skalierung und saubere Tracking-Strukturen sind nur begrenzt möglich.",
];

export default function AudienceSwitch() {
  const [audience, setAudience] = useState<"small" | "enterprise">("small");

  return (
    <section className="py-20 bg-[rgb(var(--bg-section))]" id="engineering">
      <div className="container mx-auto px-4 md:px-8">
        <div className="mx-auto max-w-4xl text-center space-y-4">
          <div className="inline-flex items-center justify-center gap-2 rounded-full border border-[#1B3A60]/30 bg-[rgb(var(--bg-surface))] px-4 py-2 text-xs uppercase tracking-[0.3em] text-[rgb(var(--text-muted))]">
            Erklärung nach Wissensstand
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-[rgb(var(--text-primary))]">
            Einfach erklärt oder Deep Dive?
          </h2>
          <p className="text-base text-[rgb(var(--text-muted-strong))]">
            Wählen Sie, wie tief wir einsteigen sollen – das Ergebnis bleibt immer klar: bessere Sichtbarkeit und mehr Anfragen.
          </p>
        </div>

        <div className="mt-8 flex justify-center">
          <div className="inline-flex rounded-full border border-[#1B3A60]/30 bg-[rgb(var(--bg-surface))] p-1">
            {[
              { key: "small", label: "Einfach erklärt" },
              { key: "enterprise", label: "Deep Dive" },
            ].map((option) => (
              <button
                key={option.key}
                type="button"
                onClick={() => setAudience(option.key as "small" | "enterprise")}
                className={`px-4 py-2 text-sm font-semibold rounded-full transition-colors ${
                  audience === option.key
                    ? "bg-[#1B3A60] text-[rgb(var(--text-on-accent))]"
                    : "text-[rgb(var(--text-muted-strong))] hover:text-[#1B3A60]"
                }`}
                aria-pressed={audience === option.key}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        {audience === "small" ? (
          <div className="mt-10 grid gap-6 lg:grid-cols-[1.1fr_0.9fr]">
            <div className="rounded-3xl border border-[#1B3A60]/20 bg-[rgb(var(--bg-base))] p-6 space-y-4">
              <h3 className="text-2xl font-semibold text-[rgb(var(--text-primary))]">
                Einfach erklärt
              </h3>
              <p className="text-sm text-[rgb(var(--text-muted-strong))]">
                Für Entscheider ohne Technik-Hintergrund: Wir entwickeln Ihre Website neu, damit sie Anfragen bringt.
              </p>
              <ul className="space-y-3 text-sm text-[rgb(var(--text-muted-strong))]">
                {smallCompanyBenefits.map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-[#10B981] mt-0.5" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-3xl border border-[#C9B37E]/30 bg-gradient-to-br from-[#1B3A60]/10 to-[#C9B37E]/10 p-6 space-y-4">
              <h3 className="text-xl font-semibold text-[rgb(var(--text-primary))]">
                Warum der Preis fair ist
              </h3>
              <p className="text-sm text-[rgb(var(--text-muted-strong))]">
                Unsere Preise decken Entwicklung, Betrieb und laufende Optimierung ab – damit Sie planbar Ergebnisse sehen.
              </p>
              <ul className="space-y-2 text-sm text-[rgb(var(--text-muted-strong))]">
                <li className="flex items-start gap-3">
                  <BarChart3 className="h-5 w-5 text-[#C9B37E] mt-0.5" />
                  <span>Neuaufbau durch Spezialisten statt Baukasten-Kompromiss.</span>
                </li>
                <li className="flex items-start gap-3">
                  <MonitorCheck className="h-5 w-5 text-[#C9B37E] mt-0.5" />
                  <span>Monitoring, Wartung und schnelle Reaktionen bei Problemen.</span>
                </li>
                <li className="flex items-start gap-3">
                  <Zap className="h-5 w-5 text-[#C9B37E] mt-0.5" />
                  <span>Performance-Optimierung für Sichtbarkeit und Anfragen.</span>
                </li>
              </ul>
              <Link
                href="/preise"
                className="inline-flex items-center gap-2 text-sm font-semibold text-[#1B3A60] hover:text-[#2D5A8C]"
              >
                Preise im Detail ansehen
              </Link>
            </div>
          </div>
        ) : (
          <div className="mt-10 grid gap-6 lg:grid-cols-[1.1fr_0.9fr]">
            <div className="rounded-3xl border border-[#1B3A60]/20 bg-[rgb(var(--bg-base))] p-6 space-y-4">
              <h3 className="text-2xl font-semibold text-[rgb(var(--text-primary))]">
                Engineering, DevOps und SEO-Performance
              </h3>
              <p className="text-sm text-[rgb(var(--text-muted-strong))]">
                Für Technik-Interessierte: Wir gehen tief in Technologie, Stabilität und Betrieb.
              </p>
              <ul className="space-y-3 text-sm text-[rgb(var(--text-muted-strong))]">
                {enterpriseStack.map((item) => (
                  <li key={item.label} className="flex items-start gap-3">
                    <item.icon className="h-5 w-5 text-[#1B3A60] mt-0.5" />
                    <span>{item.label}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-3xl border border-[#E63946]/20 bg-[#E63946]/5 p-6 space-y-4">
              <h3 className="text-xl font-semibold text-[rgb(var(--text-primary))]">
                Warum nicht WordPress oder Baukasten?
              </h3>
              <ul className="space-y-3 text-sm text-[rgb(var(--text-muted-strong))]">
                {wordpressLimits.map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <ShieldCheck className="h-5 w-5 text-[#E63946] mt-0.5" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <div className="rounded-2xl border border-[#1B3A60]/20 bg-[rgb(var(--bg-surface))] p-4 text-sm text-[rgb(var(--text-muted-strong))]">
                Der monatliche Preis erklärt sich durch Entwicklung, Betrieb, Monitoring und kontinuierliche Optimierung.
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
